import { ErrorModal, HelmetDexteel } from "@dexteel/mesf-core";
import PrintIcon from "@mui/icons-material/Print";
import {
	Alert,
	Box,
	Button,
	Paper,
	Skeleton,
	Tooltip,
	Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useCallback, useMemo, useState } from "react";
import {
	EmployeeField,
	EmployeeNumberProvider,
	useEmployeeNumber,
} from "@/pages/saw-tablet/employee/EmployeeNumber";
import { ShiftHeaderPill } from "@/pages/weldlines/components/ShiftHeaderPill";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { PrintToast } from "@/pages/printers/components/PrintToast";
import { liveDotSx } from "@/pages/printers/components/StatusLabel";
import { KIND_COLOR, statusInfo } from "@/pages/printers/statusInfo";
import { shownTagNo } from "@/pages/sawlines/utils/entryBarcode";
import { ATHENS_GRAY } from "@/shared/colors";
import { BundleCard } from "./components/BundleCard";
import { BundleTables } from "./components/BundleTables";
import { Card } from "./components/Card";
import { CheckCard } from "./components/CheckCard";
import {
	FLAGS,
	FlagConfirm,
	type FlagKind,
	PrintConfirm,
} from "./components/Modals";
import { NewBundleModal } from "./components/NewBundleModal";
import { TagPreview } from "./components/TagPreview";
import {
	createMillBundle,
	deleteMillBundle,
	getTagStation,
	getTagStationCatalogs,
	type MillBundle,
	type NewMillBundle,
	type PrintedTag,
	printMillBundle,
	reprintMillBundle,
	setMillBundleFlags,
	type TagStationData,
} from "./repositories/tagStationRepository";
import {
	FIELDS,
	newWork,
	official,
	printing,
	type SectionMap,
	sent,
	valuesOf,
	type Work,
} from "./tagStation";

const STATION_KEY = ["tagStation"] as const;
const CATALOGS_KEY = ["tagStationCatalogs"] as const;

type Modal =
	| { type: "flag"; kind: FlagKind; id: number }
	| { type: "print"; id: number }
	| { type: "new" };

const HeaderChip = ({
	border,
	color = border,
	live = false,
	children,
}: {
	border: string;
	color?: string;
	live?: boolean;
	children: ReactNode;
}) => (
	<Box
		sx={{
			px: 1.25,
			py: 0.4,
			borderRadius: 1,
			display: "flex",
			alignItems: "center",
			gap: 0.75,
			bgcolor: `${border}1A`,
			border: `1px solid ${border}`,
			color,
			fontSize: "0.7rem",
			fontWeight: 700,
			letterSpacing: 0.4,
			textTransform: "uppercase",
			...(live && liveDotSx),
		}}
	>
		<Box
			component="span"
			sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: "currentColor" }}
		/>
		{children}
	</Box>
);

const PrinterChip = ({ status }: { status: string | null }) => {
	const { label, kind } = statusInfo(status);
	return (
		<HeaderChip border={KIND_COLOR[kind]} live={kind === "ok"}>
			Printer · {label}
		</HeaderChip>
	);
};

const nameOf = (b: MillBundle) =>
	b.TagNo ? `Tag ${shownTagNo(b.TagNo)}` : "The bundle";

const Workspace = ({ data }: { data: TagStationData }) => {
	const queryClient = useQueryClient();
	const { requireEmployee } = useEmployeeNumber();
	const catalogs = useQuery({
		queryKey: CATALOGS_KEY,
		queryFn: getTagStationCatalogs,
		staleTime: 5 * 60 * 1000,
	});
	const sections = useMemo(
		() => catalogs.data?.sections ?? [],
		[catalogs.data],
	);
	const sectionMap = useMemo(
		(): SectionMap =>
			Object.fromEntries(sections.map((s) => [s.SectionName, s])),
		[sections],
	);
	const grades = catalogs.data?.grades ?? [];
	const heats = catalogs.data?.heats ?? [];

	const [selectedId, setSelectedId] = useState<number | null>(null);
	const [works, setWorks] = useState<Record<number, Work>>({});
	const [modal, setModal] = useState<Modal | null>(null);
	const [itemId, setItemId] = useState<number | null>(null);
	const [snack, setSnack] = useState<{
		message: string;
		severity: "success" | "warning";
	} | null>(null);
	const notify = (
		message: string,
		severity: "success" | "warning" = "success",
	) => setSnack({ message, severity });
	const [error, setError] = useState("");

	// every write goes through here
	const save = useMutation<unknown, Error, () => Promise<unknown>>({
		mutationFn: (write) => write(),
		onError: (e) => setError(e.message),
		onSettled: () => queryClient.invalidateQueries({ queryKey: STATION_KEY }),
	});

	const { bundles } = data;
	const byId = (id: number) => bundles.find((b) => b.MillBundleId === id);
	const waiting = bundles.filter((b) => b.State !== "Printed");
	const current =
		(selectedId != null && byId(selectedId)) ||
		waiting.find((b) => b.State === "Station") ||
		waiting[0] ||
		null;

	const workOf = (b: MillBundle) =>
		(b.State !== "Printed" && works[b.MillBundleId]) || newWork(b);
	const valuesFor = (b: MillBundle) =>
		b.State === "Printed" ? official(b) : valuesOf(workOf(b).draft);
	const missingOf = (b: MillBundle) =>
		b.State === "Printed"
			? []
			: FIELDS.map(([f]) => f).filter((f) => !workOf(b).confirmed[f]);
	const setWork = (b: MillBundle, work: Work) =>
		setWorks((all) => ({ ...all, [b.MillBundleId]: work }));

	const select = useCallback(
		(b: MillBundle) => setSelectedId(b.MillBundleId),
		[],
	);
	const askPrint = useCallback(
		(b: MillBundle) => setModal({ type: "print", id: b.MillBundleId }),
		[],
	);
	const askScrap = useCallback(
		(b: MillBundle) =>
			setModal({
				type: "flag",
				kind: b.IsScrap ? "unscrap" : "scrap",
				id: b.MillBundleId,
			}),
		[],
	);

	const askDelete = useCallback(
		(b: MillBundle) =>
			setModal({ type: "flag", kind: "delete", id: b.MillBundleId }),
		[],
	);

	const flag = (b: MillBundle, kind: FlagKind) =>
		requireEmployee((employee) => {
			const { flags, done } = FLAGS[kind];
			save.mutate(
				() =>
					flags
						? setMillBundleFlags(b.MillBundleId, flags, employee)
						: deleteMillBundle(b.MillBundleId, employee),
				{ onSuccess: () => notify(`${nameOf(b)} ${done}`) },
			);
		});
	const print = (b: MillBundle) => {
		const reprint = b.State === "Printed";
		requireEmployee((employee) =>
			save.mutate(
				() =>
					reprint
						? reprintMillBundle(b.MillBundleId, employee)
						: printMillBundle(
								b.MillBundleId,
								valuesFor(b),
								workOf(b).draft.comment.trim(),
								employee,
							),
				{
					onSuccess: (r) => {
						setItemId((r as PrintedTag).printQueueItemId);
						if (reprint) return;
						setWorks(({ [b.MillBundleId]: _, ...rest }) => rest);
						setSelectedId(null);
					},
				},
			),
		);
	};
	const create = (bundle: NewMillBundle) =>
		requireEmployee((employee) =>
			save.mutate(() => createMillBundle(bundle, employee), {
				onSuccess: () => {
					setModal(null);
					setSelectedId(null);
					notify(
						`Bundle added ${bundle.where === "Station" ? "at the station" : "as set aside"}`,
					);
				},
			}),
		);

	const onPrintError = useCallback((e: Error) => setError(e.message), []);
	// heats rolled since the page loaded join the list
	const openNew = () => {
		void catalogs.refetch();
		setModal({ type: "new" });
	};
	const open = modal && "id" in modal ? byId(modal.id) : undefined;
	const printed = current?.State === "Printed";
	const blocked = current ? missingOf(current).length > 0 : true;
	const queued = current != null && printing(current);

	return (
		<>
			<Box
				sx={{
					flex: 1,
					minHeight: 0,
					display: "grid",
					gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
					// half each, with or without a bundle; the top never goes under its content
					gridTemplateRows: "minmax(min-content, 1fr) minmax(0, 1fr)",
					gap: 1,
				}}
			>
				{current ? (
					<>
						<BundleCard
							bundle={current}
							values={valuesFor(current)}
							sections={sectionMap}
							onAside={() =>
								setModal({
									type: "flag",
									kind: current.State === "Aside" ? "unaside" : "aside",
									id: current.MillBundleId,
								})
							}
							onScrap={() => askScrap(current)}
						/>
						<CheckCard
							bundle={current}
							work={workOf(current)}
							sections={sections}
							grades={grades}
							heats={heats}
							comments={data.comments}
							onWork={(work) => setWork(current, work)}
							onInvalid={(message) => notify(message, "warning")}
						/>
						<Card title="Tag">
							<Box
								sx={{
									flex: 1,
									minHeight: 0,
									display: "flex",
									flexDirection: "column",
									gap: 1.5,
									p: 1.5,
								}}
							>
								<Box
									sx={{
										flex: 1,
										display: "flex",
										alignItems: "center",
										containerType: "size",
									}}
								>
									<TagPreview
										values={valuesFor(current)}
										tagNo={current.TagNo}
										date={current.PrintedAt}
										scrap={current.IsScrap}
										sections={sectionMap}
										base={printed ? undefined : sent(current)}
										baseLabel={current.IsManual ? "Entered" : "Mill sent"}
										// as tall as its box, at the tag's proportion
										width="calc(100cqh * 1.43)"
									/>
								</Box>
								<Tooltip
									title={
										queued
											? "Already waiting to print"
											: blocked && !printed
												? "Confirm the 5 values first"
												: ""
									}
								>
									<Box component="span" sx={{ display: "flex" }}>
										<Button
											data-print-tag
											variant="contained"
											size="large"
											fullWidth
											startIcon={<PrintIcon />}
											disabled={
												(blocked && !printed) || queued || save.isLoading
											}
											onClick={() => askPrint(current)}
											sx={{ height: 52, fontSize: "1.15rem" }}
										>
											{printed ? "Reprint" : "Print"}
										</Button>
									</Box>
								</Tooltip>
							</Box>
						</Card>
					</>
				) : (
					<Paper
						sx={{
							gridColumn: "1 / -1",
							display: "flex",
							flexDirection: "column",
							justifyContent: "center",
							p: 3,
							borderRadius: 2,
							color: "text.secondary",
							textAlign: "center",
						}}
					>
						Nothing at the station.
						<br />
						Add the bundle with New bundle, in To print, when it arrives.
					</Paper>
				)}
				<BundleTables
					bundles={bundles}
					currentId={current?.MillBundleId ?? null}
					onNew={openNew}
					onSelect={select}
					onPrint={askPrint}
					onScrap={askScrap}
					onDelete={askDelete}
				/>
			</Box>

			{modal?.type === "flag" && open && (
				<FlagConfirm
					kind={modal.kind}
					bundle={open}
					values={valuesFor(open)}
					onClose={(confirmed) => {
						setModal(null);
						if (confirmed) flag(open, modal.kind);
					}}
				/>
			)}
			{modal?.type === "print" && open && (
				<PrintConfirm
					bundle={open}
					values={valuesFor(open)}
					sections={sectionMap}
					missing={missingOf(open)}
					opened={open === current}
					nextTagNo={data.header?.NextTagNo ?? null}
					onClose={(result) => {
						setModal(null);
						if (result === "edit") select(open);
						else if (result) print(open);
					}}
				/>
			)}
			{modal?.type === "new" && (
				<NewBundleModal
					header={data.header}
					sections={sections}
					grades={grades}
					heats={heats}
					heatsLoading={catalogs.isFetching}
					comments={data.comments}
					sourceNotes={data.sourceNotes}
					saving={save.isLoading}
					onClose={(bundle) => (bundle ? create(bundle) : setModal(null))}
				/>
			)}
			{itemId != null && (
				<PrintToast
					printQueueItemId={itemId}
					onClose={() => setItemId(null)}
					onError={onPrintError}
					onItemChange={setItemId}
				/>
			)}
			<NotificationSnackBar
				message={snack?.message ?? null}
				severity={snack?.severity}
				onHide={() => setSnack(null)}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};

export const TagStationPage = () => {
	const station = useQuery<TagStationData, Error>({
		queryKey: STATION_KEY,
		queryFn: getTagStation,
		meta: { realtime: ["FAB.GetTagStation"] },
		refetchInterval: 10000,
		refetchOnWindowFocus: true,
		retry: false,
	});

	return (
		<>
			<HelmetDexteel title="Tag Station" />
			<EmployeeNumberProvider shiftId={station.data?.header?.CurrShiftId}>
				<Box
					sx={{
						height: { xs: "calc(100dvh - 56px)", sm: "calc(100dvh - 64px)" },
						display: "flex",
						flexDirection: "column",
						gap: 1,
						p: 1.5,
						overflow: "hidden",
						backgroundColor: ATHENS_GRAY,
					}}
				>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 1,
							flexWrap: "wrap",
							flexShrink: 0,
						}}
					>
						<Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
							Tag Station
						</Typography>
						<HeaderChip border="#F2B600" color="#8a5a00">
							NO PLC · MANUAL ENTRY
						</HeaderChip>
						<Box sx={{ flexGrow: 1 }} />
						<PrinterChip status={station.data?.header?.PrinterStatus ?? null} />
						<ShiftHeaderPill
							today={new Date()}
							header={station.data?.header}
							loading={station.isLoading}
						/>
						<EmployeeField />
					</Box>
					{station.error && (
						<Alert
							severity="error"
							action={
								<Button onClick={() => void station.refetch()}>Retry</Button>
							}
						>
							{station.error.message}
						</Alert>
					)}
					{station.data ? (
						<Workspace data={station.data} />
					) : (
						station.isLoading && (
							<Box role="status" aria-label="Loading Tag Station">
								<Skeleton variant="rounded" height={72} />
								<Skeleton variant="rounded" height={320} sx={{ mt: 1 }} />
							</Box>
						)
					)}
				</Box>
			</EmployeeNumberProvider>
		</>
	);
};
